/**
 * Canonical product-analytics taxonomy — the single source of truth for every
 * PostHog event name, super-property, person-property, and group-property used
 * across the three first-party Syncanix clients:
 *
 *   - the dashboard      (`apps/web`)
 *   - the marketing site (`apps/marketing`)
 *   - the discovery CLI  (`packages/cli`)
 *
 * The embedded chat widget (`apps/widget`) is deliberately OUT of scope — it is
 * an end-user surface embedded on customer sites, not a Syncanix product
 * surface, and instrumenting it would mix our customers' end-users into our own
 * product analytics.
 *
 * This module is PURE data + types — it imports no analytics SDK. Each surface
 * owns its own PostHog client (`posthog-js` in the browser, `posthog-node` in
 * the CLI) and imports these names so the three surfaces can never drift on a
 * string. A typo in an event name is the single most common way an analytics
 * dataset rots; centralising the names here makes that a type error.
 *
 * Naming conventions (PostHog house style):
 *   - event names: lower `snake_case`, namespaced by surface/funnel stage.
 *   - property keys: lower `snake_case`.
 *   - NEVER put PII (raw email, name, IP, full URLs with query strings) in a
 *     payload. Ids and enums only. See `.claude/rules/logging.md` +
 *     `.claude/rules/security.md`.
 */

/** The three first-party clients, used as the `app` super-property. */
export const ANALYTICS_APPS = {
  DASHBOARD: 'dashboard',
  MARKETING: 'marketing',
  CLI: 'cli',
} as const;

export type AnalyticsApp = (typeof ANALYTICS_APPS)[keyof typeof ANALYTICS_APPS];

/**
 * Canonical event names. Grouped by funnel stage so the spread reads as the
 * product story: acquire (marketing) → activate (cli) → configure (dashboard)
 * → monetize. The PostHog dashboard insights query these exact strings.
 */
export const ANALYTICS_EVENTS = {
  // ── Acquisition — marketing site (public, top of funnel) ──────────────────
  /** A "Start free" / "Sign up" CTA was clicked. `location` says which one. */
  MARKETING_SIGNUP_CTA_CLICKED: 'marketing_signup_cta_clicked',
  /** A "scan your site" entry link to the public `/scan` funnel was clicked (marketing→scan bridge). `location` says which one. */
  MARKETING_SCAN_CTA_CLICKED: 'marketing_scan_cta_clicked',
  /**
   * The non-developer "see it work" CTA was clicked — the landing hero's PRIMARY
   * action since the 2026-07 redesign. Distinguishing it from the signup CTA is
   * the point: the funnel could previously only see engineer-shaped intent
   * (sign up / scan), so a VP Product who watched the demo and left looked
   * identical to a bounce.
   */
  MARKETING_DEMO_CTA_CLICKED: 'marketing_demo_cta_clicked',
  /** A "talk to us" / contact CTA was clicked from a marketing surface. */
  MARKETING_CONTACT_CTA_CLICKED: 'marketing_contact_cta_clicked',
  /** B20 - the marketing ErrorBoundary caught a render crash (operator visibility). */
  MARKETING_ERROR_BOUNDARY_CAUGHT: 'marketing_error_boundary_caught',
  /** The pricing page was viewed (richer than a raw $pageview: carries plan context). */
  MARKETING_PRICING_VIEWED: 'marketing_pricing_viewed',
  /** A specific plan's CTA was chosen on the pricing page. */
  MARKETING_PLAN_SELECTED: 'marketing_plan_selected',
  /** A `/vs/<rival>` comparison page was viewed. */
  MARKETING_COMPARISON_VIEWED: 'marketing_comparison_viewed',
  /** The embedded ROI calculator produced a result. */
  MARKETING_ROI_CALCULATED: 'marketing_roi_calculated',
  /**
   * A landing section's "how this actually works" disclosure was opened.
   *
   * The redesign's central bet is layered depth: every section leads in plain
   * language and hides its engineer detail behind one toggle, so a growth lead
   * and a staff engineer can read the same page. This is the only way to find
   * out whether that is true — a disclosure nobody opens is dead weight, and one
   * everybody opens belongs inline.
   */
  MARKETING_DEPTH_DISCLOSED: 'marketing_depth_disclosed',

  // ── Activation — discovery CLI (`npx syncanix init`) ──────────────────────
  /** `syncanix init` started (after telemetry consent). */
  CLI_INIT_STARTED: 'cli_init_started',
  /** Static capability scan finished — carries detected framework + capability counts. */
  CLI_SCAN_COMPLETED: 'cli_scan_completed',
  /** The generated catalog was uploaded to Syncanix cloud — the activation moment. */
  CLI_CATALOG_UPLOADED: 'cli_catalog_uploaded',
  /** `syncanix init` failed — carries a coarse error category, never a stack trace. */
  CLI_INIT_FAILED: 'cli_init_failed',
  /** The enrichment consent decision — the biggest activation drop-off point. */
  CLI_CONSENT_DECIDED: 'cli_consent_decided',
  /** The scan discovered zero capabilities — a top no-findings dead-end. */
  CLI_SCAN_ZERO_FINDINGS: 'cli_scan_zero_findings',
  /** The user declined the catalog-upload consent (distinct from `--offline`). */
  CLI_UPLOAD_DECLINED: 'cli_upload_declined',

  // ── Activation — the interactive console + kit (ADR-0439 / ADR-0440) ──────
  /** A bare `syncanix` opened the console at a real terminal. */
  CLI_CONSOLE_OPENED: 'cli_console_opened',
  /**
   * A console screen was entered. `screen` is the closed `ScreenId` vocabulary
   * (`catalog`, `keys`, …) — never a label, a path, or anything the operator
   * typed, so the property stays low-cardinality and carries no content.
   */
  CLI_CONSOLE_SCREEN_VIEWED: 'cli_console_screen_viewed',
  /** The `syncanix/` kit was written — COUNTS only, never file names or bodies. */
  CLI_KIT_GENERATED: 'cli_kit_generated',

  // ── Onboarding & lifecycle — dashboard ────────────────────────────────────
  /**
   * An anonymous visitor landed on `/signin` (e.g. from a marketing CTA)
   * BEFORE the Auth0 hosted-login redirect. Captured instantly so the
   * sub-2s auto-redirect can't drop it — this is the dashboard-side bridge
   * of the acquisition funnel that `marketing_signup_cta_clicked` opens.
   */
  SIGNIN_PAGE_VIEWED: 'signin_page_viewed',
  USER_SIGNED_IN: 'user_signed_in',
  WORKSPACE_CREATED: 'workspace_created',
  WORKSPACE_SWITCHED: 'workspace_switched',

  // ── Public /scan funnel — dashboard origin, pre-auth (A24/AF-133) ─────────
  /** Anonymous landing on the public `/scan` page (top-of-funnel wow moment). */
  SCAN_PAGE_VIEWED: 'scan_page_viewed',
  /** A scan was submitted (after client-side URL validation passed). */
  SCAN_SUBMITTED: 'scan_submitted',
  /** The scan returned a result and the themed preview rendered. */
  SCAN_SUCCEEDED: 'scan_succeeded',
  /** The scan failed — carries the typed refusal class, never the URL. */
  SCAN_FAILED: 'scan_failed',
  /** The visitor copied the embed snippet. */
  SCAN_EMBED_COPIED: 'scan_embed_copied',
  /** The visitor clicked the create-workspace CTA from the scan result. */
  SCAN_SIGNUP_CTA_CLICKED: 'scan_signup_cta_clicked',

  // ── Configuration / feature adoption — dashboard ──────────────────────────
  CATALOG_VIEWED: 'catalog_viewed',
  CAPABILITY_VIEWED: 'capability_viewed',
  API_KEY_CREATED: 'api_key_created',
  WIDGET_DESIGN_PUBLISHED: 'widget_design_published',
  DOCS_SOURCE_CONNECTED: 'docs_source_connected',
  /**
   * The empty documentation hub's primary action was taken — DE-306's clock
   * STARTS here.
   *
   * The phase headline is "empty hub to first readable article in fifteen
   * minutes", and for the login-walled ICP that window is exactly when a
   * prospect is deciding: comprehension accrues from in-session harvest and the
   * widget goes live at the END of onboarding, so the hub is empty precisely
   * during evaluation. A claim like that has to be measured on real workspaces
   * rather than asserted from a demo, which is what this pair is for.
   */
  DOCS_FIRST_ARTICLES_STARTED: 'docs_first_articles_started',
  /**
   * The hub that was empty now has an article on screen — DE-306's clock STOPS.
   *
   * `elapsed_ms` measures the PLATFORM's part of the promise: from the CTA to
   * an article being readable. It deliberately excludes how long the operator
   * spent inside the walk deciding what to click, because that is their time
   * and folding it in would make the number say something about them rather
   * than about us. `walk_completed` records whether a walk actually ran in
   * between, so a workspace whose articles arrived another way is separable
   * rather than silently inflating the sample.
   */
  DOCS_FIRST_ARTICLES_READY: 'docs_first_articles_ready',
  CUSTOM_ENDPOINT_CREATED: 'custom_endpoint_created',
  CUSTOM_ENDPOINT_UPDATED: 'custom_endpoint_updated',
  PERSONA_SAVED: 'persona_saved',
  /** The operator ran the "Check installation" probe — carries the result class, never the URL. */
  INSTALL_CHECK_RUN: 'install_check_run',

  // ── Monetization — dashboard ──────────────────────────────────────────────
  BILLING_PORTAL_OPENED: 'billing_portal_opened',
  PLAN_CHECKOUT_STARTED: 'plan_checkout_started',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

const EVENT_NAME_SET: ReadonlySet<string> = new Set(Object.values(ANALYTICS_EVENTS));

/** Type-guard: is an arbitrary string one of the canonical event names? */
export function isAnalyticsEventName(candidate: string): candidate is AnalyticsEventName {
  return EVENT_NAME_SET.has(candidate);
}

export type {
  AnalyticsEventPayloads,
  AnalyticsGroupProperties,
  AnalyticsPersonProperties,
  AnalyticsSuperProperties,
  PropertiesFor,
} from './event-payloads';
export { WORKSPACE_GROUP_TYPE } from './event-payloads';
